import {forkJoin, Observable, of, Subject} from 'rxjs';
import {SiteInfo} from './../../services/api/access-sites.interface';
import {SiteService} from '@shared/services/api/site.service';
import {AceSelectComponent} from './../ace-select/ace-select.component';
import {LevelZoneService} from '@app/layout/sites/manage/manage-sites/upsert-site/forms/level-zone-form/level-zone.service';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {Component, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {Level, LevelId} from '@app/graphql-typings/graphql';
import {AceLogger} from '@logging/ace-logger';
import {AceLoggerService} from '@logging/ace-logger.service';
import {finalize} from "rxjs/operators";

@Component({
  selector: 'app-levels-select',
  templateUrl: './levels-select.component.html',
  styleUrls: ['./levels-select.component.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => LevelsSelectComponent), multi: true},
  ],
  standalone: false
})
export class LevelsSelectComponent implements OnInit, OnDestroy, ControlValueAccessor, OnChanges {

  @ViewChild('levelSelect', { static: true }) levelSelect: AceSelectComponent;
  @Input() bindToLevelId: boolean = false;
  @Input() siteIds: number[];
  @Input() levels: Level[];
  @Input() multiSelect = false;
  @Input() showSite = false;
  @Input() placeholder: string;
  @Input() appendTo: string;
  @Input() hideExcluded = false;
  @Input() showSelectAll = false;
  @Input() selectAllOnSiteChange = false;
  @Input() maxSelectedLabels = 3;
  @Input() loading = false;

  @Output() loadedLevels = new EventEmitter<Level[]>();
  @Output() openEvent = new EventEmitter<void>();
  @Output() closeEvent = new EventEmitter<void>();

  private readonly logger: AceLogger;
  private readonly ngUnsubscribe: Subject<void> = new Subject();

  levelsInitialised = false;
  readonly MAX_ITEMS = 3;
  levelOptions: Level[];

  private onChange = (_: any) => {};
  onTouched = () => {};

  constructor(private levelZoneService: LevelZoneService,
              private siteService: SiteService,
              logService: AceLoggerService) {
    this.logger = logService.create(LevelsSelectComponent.name);
  }

  ngOnInit() {
    if (!this.multiSelect && this.showSelectAll) {
      throw new Error('multiSelect is false so cannot have showSelectAll true');
    }
    if (!this.multiSelect && this.selectAllOnSiteChange) {
      throw new Error('multiSelect is false so cannot have selectAllOnSiteChange true');
    }
    this.levelSelect.registerOnChange(this.aceSelectOnChange);
    this.levelSelect.registerOnTouched(this.aceSelectOnTouched);
    if (!this.levelsInitialised) {
      this.loadSitesAndLevels();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.logger.debug('ngOnChanges change', changes);
    if (changes.levels || changes.siteIds) {
      this.levelsInitialised = true;
      this.loadSitesAndLevels();
    }
  }

  private aceSelectOnChange = (selected: any) => {
    this.onChange(selected);
  };

  private aceSelectOnTouched = () => {
    this.onTouched();
  };

  private loadSitesAndLevels() {
    if (this.siteIds) {
      this.loading = true;
      const sites$: Observable<SiteInfo[]> = this.showSite ? this.siteService.getCachedSiteInfos(this.siteIds) : of([]);
      let levels$: Observable<Level[]>;
      if (this.levels == null) {
        levels$ = this.levelZoneService.getLevelsBySiteIds(this.siteIds);
      } else {
        levels$ = of(this.levels);
      }

      forkJoin([sites$, levels$]).pipe(finalize(() => this.loading = false)).subscribe(([siteInfos, levels]: [SiteInfo[], Level[]]) => {
        if (levels && levels.length > 0) {
          this.levelOptions = this.addSiteNameToLevels(this.removeExcludedLevels(levels), siteInfos);
          this.levelOptions.sort(this.sortLevelsByName);
          this.loadedLevels.next(this.levelOptions);
          if (this.selectAllOnSiteChange) {
            this.selectLevels(this.levelOptions);
          }
        }
      });
    } else {
      this.levelOptions = null;
      this.writeValue(null);
    }
  }


  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  writeValue(value: Level | Level[] | LevelId | LevelId[]): void {
    if (value && Array.isArray(value) && !this.multiSelect) {
      throw new Error('multiSelect is false so expected single object not array');
    } else if (value && !Array.isArray(value) && this.multiSelect) {
      throw new Error('multiSelect is true so expected array not single object');
    } else {
      this.levelSelect.writeValue(value);
    }
  }


  registerOnChange(fn: any): void {
    this.onChange = fn;
  }


  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }


  setDisabledState(isDisabled: boolean) {
    this.levelSelect.setDisabledState(isDisabled);
  }

  onOpen(): void {
    this.openEvent.emit();
  }

  onClose(): void {
    this.closeEvent.emit();
  }

  selectAll(event) {
    event.preventDefault();
    this.selectLevels(this.levelOptions);
  }

  private selectLevels(levels: Level[]) {
    this.writeValue(this.bindToLevelId ? levels.map(level => level.level_id) : levels);
  }

  private addSiteNameToLevels(levels: Level[], siteInfos: SiteInfo[]): Level[] {
    levels.forEach((level: Level) => {
      const siteInfo: SiteInfo = siteInfos.find(s => s.site_id === level.site_id);
      level['site_name'] = siteInfo ? siteInfo.site_name : null;
    });
    return levels;
  }

  private removeExcludedLevels(levels: Level[]): Level[] {
    const includedLevels: Level[] = [];
    if (this.hideExcluded) {
      levels.forEach((level: Level) => {
        if (!level.thermalcomfort_excluded) {
          includedLevels.push(level);
        }
      });
      return includedLevels;
    } else {
      return levels;
    }
  }

  private sortLevelsByName = (a: Level, b: Level) => a.level_name.trim().localeCompare(b.level_name.trim());
}
